import { z } from 'zod'

import { normalizeGuestMedia } from './guestMedia'
import { haveEqualGuestNetworks, normalizeGuestNetworks } from './guestNetworks'
import type { GuestNetworkId } from './guestNetworks'
import type { GuestMedia } from './guestMedia'
import { haveEqualGuestTags, normalizeGuestTags } from './guestTags'
import type { GuestTag } from './guestTags'

const LEGACY_STORAGE_KEY = 'buffer:publish:guest-drafts'
const STORAGE_KEY = 'buffer:publish:guest-drafts:v3'
const MIN_STORAGE_VERSION = 3
const STORAGE_VERSION = 4
const MAX_DRAFTS = 10
const DRAFT_TTL_MS = (30 * 24 - 1) * 60 * 60 * 1000
export const GUEST_DRAFT_TEXT_MAX_LENGTH = 10_000

const StoredGuestDraftFields = z
  .object({
    id: z.string().refine((id) => id.trim().length > 0),
    text: z.string(),
    media: z.array(z.unknown()),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict()

const StoredGuestDraftSchema = StoredGuestDraftFields.refine(
  ({ createdAt, updatedAt }) => createdAt <= updatedAt,
)

const LegacyGuestDraftSchema = z
  .object({
    id: z.string().refine((id) => id.trim().length > 0),
    text: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict()
  .refine(({ createdAt, updatedAt }) => createdAt <= updatedAt)

const GuestDraftEnvelopeSchema = z
  .object({
    version: z.literal(2),
    drafts: z.array(z.unknown()),
  })
  .strict()

const LegacyGuestDraftEnvelopeSchema = z
  .object({
    version: z.literal(1),
    drafts: z.array(z.unknown()),
  })
  .strict()

const TaggedGuestDraftSchema = z
  .looseObject({
    ...StoredGuestDraftFields.shape,
    tags: z.unknown().optional(),
    networks: z.unknown().optional(),
    scheduledAt: z.unknown().optional(),
  })
  .refine(({ createdAt, updatedAt }) => createdAt <= updatedAt)

const LegacyRevisionSchema = z.object({
  id: z.string(),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
})

const TaggedGuestDraftEnvelopeSchema = z.looseObject({
  version: z.number().int().min(MIN_STORAGE_VERSION),
  drafts: z.array(z.unknown()),
  legacyRevisions: z.array(LegacyRevisionSchema).default([]),
})

type LegacyRevision = z.infer<typeof LegacyRevisionSchema>

type UnknownFields = Readonly<Record<string, unknown>>

export type GuestDraft = Readonly<{
  id: string
  text: string
  media: readonly GuestMedia[]
  tags?: readonly GuestTag[]
  networks?: readonly GuestNetworkId[]
  scheduledAt?: number
  createdAt: number
  updatedAt: number
}>

export type GuestDraftContent = Readonly<{
  text: string
  media: readonly GuestMedia[]
  tags?: readonly GuestTag[]
  networks?: readonly GuestNetworkId[]
  scheduledAt?: number | null
}>

export type GuestDraftSnapshot = Readonly<{
  drafts: readonly GuestDraft[]
  canCreateDraft: boolean
}>

export type SaveGuestDraftResult =
  | { status: 'saved'; draft: GuestDraft }
  | { status: 'unchanged'; draft: GuestDraft }
  | { status: 'conflict'; draft: GuestDraft }
  | { status: 'expired' }
  | { status: 'empty' }
  | { status: 'deleted' }
  | { status: 'limit-reached' }
  | { status: 'too-long' }
  | { status: 'read-failed' }
  | { status: 'write-failed' }

export type DeleteGuestDraftResult =
  | { status: 'deleted' }
  | { status: 'not-found' }
  | { status: 'conflict'; draft: GuestDraft }
  | { status: 'read-failed' }
  | { status: 'write-failed' }

export type GuestDraftStore = Readonly<{
  getSnapshot: () => GuestDraftSnapshot
  getById: (id: string) => GuestDraft | undefined
  save: (
    id: string | undefined,
    content: GuestDraftContent,
    expectedUpdatedAt?: number,
  ) => SaveGuestDraftResult
  saveText: (
    id: string | undefined,
    text: string,
    expectedUpdatedAt?: number,
  ) => SaveGuestDraftResult
  delete: (id: string, expectedUpdatedAt?: number) => DeleteGuestDraftResult
  subscribe: (listener: () => void) => () => void
}>

type StorageAdapter = Pick<Storage, 'getItem' | 'setItem'>

type GuestDraftStoreOptions = Readonly<{
  storage: StorageAdapter
  now: () => number
  generateId: () => string
}>

type InternalGuestDraftStore = GuestDraftStore &
  Readonly<{
    refreshFromStorage: () => void
  }>

type ReadResult =
  | {
      status: 'read'
      drafts: readonly GuestDraft[]
      legacyRevisions: readonly LegacyRevision[]
      unknownFields: StoredUnknownFields
    }
  | { status: 'read-failed' }

type StoredUnknownFields = Readonly<{
  envelope: UnknownFields
  draftsById: ReadonlyMap<string, UnknownFields>
}>

type StoredState = Readonly<{
  legacyRevisions: readonly LegacyRevision[]
  unknownFields: StoredUnknownFields
}>

function compareStrings(left: string, right: string): number {
  if (left < right) return -1
  if (left > right) return 1
  return 0
}

function compareDrafts(left: GuestDraft, right: GuestDraft): number {
  if (left.updatedAt !== right.updatedAt) {
    return right.updatedAt - left.updatedAt
  }

  if (left.createdAt !== right.createdAt) {
    return right.createdAt - left.createdAt
  }

  const idComparison = compareStrings(left.id, right.id)
  if (idComparison !== 0) return idComparison

  return compareStrings(left.text, right.text)
}

function normalizeScheduledAt(value: unknown): number | undefined {
  return typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value > 0 &&
    Number.isFinite(new Date(value).getTime())
    ? value
    : undefined
}

function omitScheduledAt(draft: GuestDraft): GuestDraft {
  const { scheduledAt: _scheduledAt, ...rest } = draft
  return rest
}

export function isGuestDraftExpired(
  draft: Pick<GuestDraft, 'createdAt'>,
  currentTime: number,
): boolean {
  return currentTime >= draft.createdAt + DRAFT_TTL_MS
}

function normalizeDrafts(
  entries: readonly unknown[],
  currentTime: number,
  withTags = false,
  unknownFieldsById?: Map<string, UnknownFields>,
): readonly GuestDraft[] {
  const draftsById = new Map<string, GuestDraft>()

  for (const entry of entries) {
    const result = (
      withTags ? TaggedGuestDraftSchema : StoredGuestDraftSchema
    ).safeParse(entry)
    if (!result.success) continue

    const {
      id,
      text,
      media: storedMedia,
      tags: storedTags,
      networks: storedNetworks,
      scheduledAt: storedScheduledAt,
      createdAt,
      updatedAt,
      ...unknownFields
    }: z.infer<typeof TaggedGuestDraftSchema> = result.data
    const media = normalizeGuestMedia(storedMedia)
    const scheduledAt = normalizeScheduledAt(storedScheduledAt)
    const draft: GuestDraft = {
      id,
      text,
      updatedAt,
      ...(storedTags !== undefined
        ? { tags: normalizeGuestTags(storedTags) }
        : {}),
      ...(storedNetworks !== undefined
        ? { networks: normalizeGuestNetworks(storedNetworks) }
        : {}),
      ...(scheduledAt === undefined ? {} : { scheduledAt }),
      media,
      createdAt: Math.min(createdAt, ...media.map((asset) => asset.uploadedAt)),
    }
    if (
      (draft.text.trim().length === 0 && draft.media.length === 0) ||
      isGuestDraftExpired(draft, currentTime)
    ) {
      continue
    }

    const currentDraft = draftsById.get(draft.id)
    if (!currentDraft || compareDrafts(draft, currentDraft) < 0) {
      draftsById.set(draft.id, draft)
      unknownFieldsById?.set(draft.id, unknownFields)
    }
  }

  return Object.freeze(
    Array.from(draftsById.values())
      .toSorted(compareDrafts)
      .slice(0, MAX_DRAFTS),
  )
}

function parseDrafts(
  serializedDrafts: string | null,
  currentTime: number,
): readonly GuestDraft[] {
  if (serializedDrafts === null) return Object.freeze([])

  let parsedValue: unknown
  try {
    parsedValue = JSON.parse(serializedDrafts)
  } catch {
    return Object.freeze([])
  }

  const envelopeResult = GuestDraftEnvelopeSchema.safeParse(parsedValue)
  if (envelopeResult.success) {
    return normalizeDrafts(envelopeResult.data.drafts, currentTime)
  }

  const legacyEnvelopeResult =
    LegacyGuestDraftEnvelopeSchema.safeParse(parsedValue)
  if (!legacyEnvelopeResult.success) return Object.freeze([])

  const migratedDrafts = legacyEnvelopeResult.data.drafts.flatMap((entry) => {
    const result = LegacyGuestDraftSchema.safeParse(entry)
    return result.success ? [{ ...result.data, media: [] }] : []
  })

  return normalizeDrafts(migratedDrafts, currentTime)
}

function createSnapshot(drafts: readonly GuestDraft[]): GuestDraftSnapshot {
  return Object.freeze({
    drafts,
    canCreateDraft: drafts.length < MAX_DRAFTS,
  })
}

function haveEqualDrafts(
  left: readonly GuestDraft[],
  right: readonly GuestDraft[],
): boolean {
  if (left.length !== right.length) return false

  return left.every((draft, index) => {
    const otherDraft = right[index]
    return (
      otherDraft !== undefined &&
      draft.id === otherDraft.id &&
      draft.text === otherDraft.text &&
      haveEqualMedia(draft.media, otherDraft.media) &&
      haveEqualGuestTags(draft.tags, otherDraft.tags) &&
      haveEqualGuestNetworks(draft.networks, otherDraft.networks) &&
      draft.scheduledAt === otherDraft.scheduledAt &&
      draft.createdAt === otherDraft.createdAt &&
      draft.updatedAt === otherDraft.updatedAt
    )
  })
}

function haveEqualMedia(
  left: readonly GuestMedia[],
  right: readonly GuestMedia[],
): boolean {
  if (left.length !== right.length) return false

  return left.every((media, index) => {
    const otherMedia = right[index]
    return (
      otherMedia !== undefined &&
      media.id === otherMedia.id &&
      media.assetUrl === otherMedia.assetUrl &&
      media.fileName === otherMedia.fileName &&
      media.mimeType === otherMedia.mimeType &&
      media.size === otherMedia.size &&
      media.uploadedAt === otherMedia.uploadedAt &&
      media.expiresAt === otherMedia.expiresAt &&
      media.width === otherMedia.width &&
      media.height === otherMedia.height &&
      media.altText === otherMedia.altText
    )
  })
}

function createInternalGuestDraftStore({
  storage,
  now,
  generateId,
}: GuestDraftStoreOptions): InternalGuestDraftStore {
  const listeners = new Set<() => void>()
  let expiryTimer: ReturnType<typeof setTimeout> | undefined

  const readLatest = (): ReadResult => {
    try {
      const currentTime = now()
      const serialized = storage.getItem(STORAGE_KEY)
      let parsed: unknown
      try {
        parsed = serialized ? JSON.parse(serialized) : null
      } catch {
        parsed = null
      }
      const stored = TaggedGuestDraftEnvelopeSchema.safeParse(parsed)
      const {
        version: _version,
        drafts: storedDrafts,
        legacyRevisions: storedLegacyRevisions,
        ...unknownEnvelopeFields
      } = stored.success
        ? stored.data
        : { version: STORAGE_VERSION, drafts: [], legacyRevisions: [] }
      const unknownDraftFieldsById = new Map<string, UnknownFields>()
      const drafts = [
        ...normalizeDrafts(
          storedDrafts,
          currentTime,
          true,
          unknownDraftFieldsById,
        ),
      ]
      const legacyRevisions = new Map(
        storedLegacyRevisions
          .filter((revision) => currentTime < revision.createdAt + DRAFT_TTL_MS)
          .map((revision) => [revision.id, revision]),
      )
      const legacyDrafts = parseDrafts(
        storage.getItem(LEGACY_STORAGE_KEY),
        currentTime,
      )
      for (const legacyDraft of legacyDrafts) {
        const revision = legacyRevisions.get(legacyDraft.id)
        if (revision && revision.updatedAt >= legacyDraft.updatedAt) continue
        const index = drafts.findIndex((draft) => draft.id === legacyDraft.id)
        const current = drafts[index]
        if (current) {
          if (legacyDraft.updatedAt > current.updatedAt) {
            drafts[index] = {
              ...current,
              ...legacyDraft,
              createdAt: Math.min(current.createdAt, legacyDraft.createdAt),
            }
          }
        } else if (drafts.length < MAX_DRAFTS) {
          drafts.push(legacyDraft)
        } else {
          continue
        }
        legacyRevisions.set(legacyDraft.id, {
          id: legacyDraft.id,
          createdAt: legacyDraft.createdAt,
          updatedAt: legacyDraft.updatedAt,
        })
      }
      return {
        status: 'read',
        drafts: Object.freeze(drafts.toSorted(compareDrafts)),
        legacyRevisions: Array.from(legacyRevisions.values()),
        unknownFields: {
          envelope: unknownEnvelopeFields,
          draftsById: unknownDraftFieldsById,
        },
      }
    } catch {
      return { status: 'read-failed' }
    }
  }

  const initialRead = readLatest()
  let snapshot = createSnapshot(
    initialRead.status === 'read' ? initialRead.drafts : Object.freeze([]),
  )

  const scheduleExpiry = (): void => {
    clearTimeout(expiryTimer)
    if (listeners.size === 0 || snapshot.drafts.length === 0) return

    const expiresAt = Math.min(
      ...snapshot.drafts.map((draft) => draft.createdAt + DRAFT_TTL_MS),
    )
    expiryTimer = setTimeout(
      () => {
        publish(
          snapshot.drafts.filter((draft) => !isGuestDraftExpired(draft, now())),
        )
      },
      Math.min(Math.max(0, expiresAt - now()), 2_147_483_647),
    )
  }

  const publish = (
    drafts: readonly GuestDraft[],
    notifyOnEqual = false,
  ): void => {
    if (!haveEqualDrafts(snapshot.drafts, drafts)) {
      snapshot = createSnapshot(drafts)
      scheduleExpiry()
      listeners.forEach((listener) => listener())
      return
    }

    scheduleExpiry()
    if (notifyOnEqual) listeners.forEach((listener) => listener())
  }

  const write = (
    drafts: readonly GuestDraft[],
    { legacyRevisions, unknownFields }: StoredState,
  ): boolean => {
    try {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...unknownFields.envelope,
          version: STORAGE_VERSION,
          drafts: drafts.map((draft) => ({
            ...unknownFields.draftsById.get(draft.id),
            ...draft,
          })),
          legacyRevisions,
        }),
      )
      return true
    } catch {
      return false
    }
  }

  const deleteFromDrafts = (
    id: string,
    drafts: readonly GuestDraft[],
    storedState: StoredState,
    expectedUpdatedAt?: number,
  ): DeleteGuestDraftResult => {
    const existingDraft = drafts.find((draft) => draft.id === id)
    if (!existingDraft) {
      publish(drafts)
      return { status: 'not-found' }
    }

    if (
      expectedUpdatedAt !== undefined &&
      existingDraft.updatedAt !== expectedUpdatedAt
    ) {
      publish(drafts)
      return { status: 'conflict', draft: existingDraft }
    }

    const nextDrafts = drafts.filter((draft) => draft.id !== id)
    if (!write(nextDrafts, storedState)) return { status: 'write-failed' }

    publish(nextDrafts, true)
    return { status: 'deleted' }
  }

  const saveDraft = (
    id: string | undefined,
    resolveContent: (
      existingDraft: GuestDraft | undefined,
    ) => GuestDraftContent,
    expectedUpdatedAt?: number,
  ): SaveGuestDraftResult => {
    const readResult = readLatest()
    if (readResult.status === 'read-failed') return readResult

    const previousDraft = id
      ? snapshot.drafts.find((draft) => draft.id === id)
      : undefined
    if (previousDraft && isGuestDraftExpired(previousDraft, now())) {
      publish(readResult.drafts)
      return { status: 'expired' }
    }
    if (
      id &&
      expectedUpdatedAt !== undefined &&
      !readResult.drafts.some((draft) => draft.id === id)
    ) {
      publish(readResult.drafts)
      return { status: 'expired' }
    }

    const existingDraft = id
      ? readResult.drafts.find((draft) => draft.id === id)
      : undefined

    if (
      existingDraft &&
      expectedUpdatedAt !== undefined &&
      existingDraft.updatedAt !== expectedUpdatedAt
    ) {
      publish(readResult.drafts)
      return { status: 'conflict', draft: existingDraft }
    }

    const content = resolveContent(existingDraft)
    const media = normalizeGuestMedia(content.media)
    const tags =
      content.tags === undefined
        ? existingDraft?.tags
        : normalizeGuestTags(content.tags)
    const networks =
      content.networks === undefined
        ? existingDraft?.networks
        : normalizeGuestNetworks(content.networks)
    const scheduledAt =
      content.scheduledAt === undefined
        ? existingDraft?.scheduledAt
        : normalizeScheduledAt(content.scheduledAt)

    if (content.text.length > GUEST_DRAFT_TEXT_MAX_LENGTH) {
      publish(readResult.drafts)
      return { status: 'too-long' }
    }

    if (content.text.trim().length === 0 && media.length === 0) {
      if (!existingDraft) {
        publish(readResult.drafts)
        return { status: 'empty' }
      }

      const deleteResult = deleteFromDrafts(
        existingDraft.id,
        readResult.drafts,
        readResult,
        expectedUpdatedAt,
      )
      if (deleteResult.status === 'deleted') return deleteResult
      if (deleteResult.status === 'write-failed') return deleteResult
      return { status: 'empty' }
    }

    if (
      existingDraft?.text === content.text &&
      haveEqualMedia(existingDraft.media, media) &&
      haveEqualGuestTags(existingDraft.tags, tags) &&
      haveEqualGuestNetworks(existingDraft.networks, networks) &&
      existingDraft.scheduledAt === scheduledAt
    ) {
      publish(readResult.drafts)
      return { status: 'unchanged', draft: existingDraft }
    }

    if (!existingDraft && readResult.drafts.length >= MAX_DRAFTS) {
      publish(readResult.drafts)
      return { status: 'limit-reached' }
    }

    const currentTime = existingDraft
      ? Math.max(now(), existingDraft.updatedAt + 1)
      : now()
    const draft: GuestDraft = existingDraft
      ? {
          ...omitScheduledAt(existingDraft),
          text: content.text,
          media,
          ...(tags === undefined ? {} : { tags }),
          ...(networks === undefined ? {} : { networks }),
          ...(scheduledAt === undefined ? {} : { scheduledAt }),
          updatedAt: currentTime,
        }
      : {
          id: generateId(),
          text: content.text,
          media,
          ...(tags === undefined ? {} : { tags }),
          ...(networks === undefined ? {} : { networks }),
          ...(scheduledAt === undefined ? {} : { scheduledAt }),
          createdAt: Math.min(
            currentTime,
            ...media.map((asset) => asset.uploadedAt),
          ),
          updatedAt: currentTime,
        }

    if (isGuestDraftExpired(draft, currentTime)) {
      publish(readResult.drafts)
      return { status: 'expired' }
    }

    const nextDrafts = normalizeDrafts(
      existingDraft
        ? readResult.drafts.map((storedDraft) =>
            storedDraft.id === existingDraft.id ? draft : storedDraft,
          )
        : [...readResult.drafts, draft],
      currentTime,
      true,
    )

    if (!write(nextDrafts, readResult)) return { status: 'write-failed' }

    publish(nextDrafts, true)
    return { status: 'saved', draft }
  }

  const store: InternalGuestDraftStore = {
    getSnapshot: () => snapshot,
    getById: (id) => snapshot.drafts.find((draft) => draft.id === id),
    save: (id, content, expectedUpdatedAt) =>
      saveDraft(id, () => content, expectedUpdatedAt),
    saveText: (id, text, expectedUpdatedAt) =>
      saveDraft(
        id,
        (existingDraft) => ({
          text,
          media: existingDraft?.media ?? [],
          ...(existingDraft?.tags === undefined
            ? {}
            : { tags: existingDraft.tags }),
          ...(existingDraft?.networks === undefined
            ? {}
            : { networks: existingDraft.networks }),
        }),
        expectedUpdatedAt,
      ),
    delete: (id, expectedUpdatedAt) => {
      const readResult = readLatest()
      if (readResult.status === 'read-failed') return readResult

      return deleteFromDrafts(
        id,
        readResult.drafts,
        readResult,
        expectedUpdatedAt,
      )
    },
    subscribe: (listener) => {
      listeners.add(listener)
      scheduleExpiry()
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0) clearTimeout(expiryTimer)
      }
    },
    refreshFromStorage: () => {
      const readResult = readLatest()
      if (readResult.status === 'read') publish(readResult.drafts)
    },
  }

  return store
}

export function createGuestDraftStore(
  options: GuestDraftStoreOptions,
): GuestDraftStore {
  return createInternalGuestDraftStore(options)
}

let browserGuestDraftStore: InternalGuestDraftStore | undefined

export function getBrowserGuestDraftStore(): GuestDraftStore {
  if (browserGuestDraftStore) {
    browserGuestDraftStore.refreshFromStorage()
    return browserGuestDraftStore
  }

  if (typeof window === 'undefined') {
    throw new Error('The guest draft store requires a browser.')
  }

  const storage: StorageAdapter = {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
  }
  const store = createInternalGuestDraftStore({
    storage,
    now: Date.now,
    generateId: () => crypto.randomUUID(),
  })

  const handleStorageEvent = (event: StorageEvent): void => {
    if (
      event.key === STORAGE_KEY ||
      event.key === LEGACY_STORAGE_KEY ||
      event.key === null
    ) {
      store.refreshFromStorage()
    }
  }
  window.addEventListener('storage', handleStorageEvent)

  browserGuestDraftStore = store
  return store
}
